import { useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { useAccountDeletion, useCancelAccountDeletion } from '@features/profile/hooks/useAccountDeletion'
import {
  formatDeletionDate,
  pendingDeletionDismissKey,
  shouldPromptForPendingDeletion,
} from '@features/profile/settings/accountDeletion'
import { getSaveFriendlyError } from '@utils/errorHandling'

function wasDismissed(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function rememberDismissed(key: string) {
  try {
    sessionStorage.setItem(key, '1')
  } catch {
    // Not remembered; it asks again on the next page load.
  }
}

/**
 * Signed in to an account that is scheduled for deletion: ask, before anything else, whether that
 * is still the plan. Blocking (no backdrop or Escape close) because the whole point is that it is
 * answered. "Continue" keeps the deletion and is remembered for this tab only.
 */
export function PendingDeletionPrompt() {
  const { pending } = useAccountDeletion()
  const cancel = useCancelAccountDeletion()
  const [, force] = useState(0)

  if (!shouldPromptForPendingDeletion(pending, wasDismissed) || !pending) return null

  return (
    <Dialog open disableEscapeKeyDown aria-labelledby="pending-deletion-title" maxWidth="xs" fullWidth>
      <DialogTitle id="pending-deletion-title">Your account is being deleted</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your account will be deleted on <strong>{formatDeletionDate(pending.purgeAfter)}</strong>.
        </DialogContentText>
        {cancel.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {getSaveFriendlyError(cancel.error).message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={() => {
            rememberDismissed(pendingDeletionDismissKey(pending))
            force((n) => n + 1)
          }}
          disabled={cancel.isPending}
        >
          Continue
        </Button>
        <Button
          variant="contained"
          onClick={() => cancel.mutate()}
          disabled={cancel.isPending}
          startIcon={cancel.isPending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Cancel deletion
        </Button>
      </DialogActions>
    </Dialog>
  )
}
